/**
 * Cliëntweergave bij beeldbellen. De cliënt opent een link, klikt één keer en
 * hoeft daarna niets meer te doen; de behandelaar bedient alles op afstand.
 *
 * PRIVACY: geen login, geen invoer. Terug naar de behandelaar gaat alleen
 * technische status (klaar, zichtbaar, volledig scherm, beelden per seconde).
 */
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import { useDocumentTitle } from "@jari-tools/shared";
import bodyHtml from "./emdr-body.html?raw";
import "./design-colors.css";
import "./style.css";
import "./figures.css";
import "./tool.css";
import "./racebaan.css";
import "./danspas.css";
import "./beeldbellen.css";
import type { WsConnectionStatus } from "./engine/ws-client";
import {
  bootstrapViewerEmdr,
  connectViewer,
  disconnectViewer,
  sendViewerReport,
} from "./engine/bootstrap-viewer";
import type { ViewerReport } from "./lib/beeldbellen-status";

const CLIENT_TITLE = "EMDR";
const REPORT_INTERVAL_MS = 4000;
const FPS_WINDOW_MS = 2000;

type WakeLockSentinelLike = { release: () => Promise<void> };

/** Meet de beelden per seconde zolang het tabblad zichtbaar is. */
function useFpsMeter(): MutableRefObject<number | null> {
  const fpsRef = useRef<number | null>(null);

  useEffect(() => {
    let rafId = 0;
    let frames = 0;
    let windowStart = performance.now();

    const tick = (now: number) => {
      frames += 1;
      const elapsed = now - windowStart;
      if (elapsed >= FPS_WINDOW_MS) {
        fpsRef.current = Math.round((frames * 1000) / elapsed);
        frames = 0;
        windowStart = now;
      }
      rafId = requestAnimationFrame(tick);
    };

    const onVisibility = () => {
      // Op de achtergrond staat de teller stil; begin daarna met een schone meting.
      frames = 0;
      windowStart = performance.now();
      if (document.visibilityState !== "visible") fpsRef.current = null;
    };

    rafId = requestAnimationFrame(tick);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return fpsRef;
}

export function ClientApp({ code }: { code: string }) {
  useDocumentTitle(CLIENT_TITLE);
  const hostRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  /** Na de eerste start blijft het scherm bij pauze leeg, zodat niets afleidt. */
  const [hasPlayed, setHasPlayed] = useState(false);
  const [sudOpen, setSudOpen] = useState(false);
  const [ended, setEnded] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [wsStatus, setWsStatus] = useState<WsConnectionStatus>("idle");
  const readyRef = useRef(false);
  const fpsRef = useFpsMeter();
  const wakeLockRef = useRef<WakeLockSentinelLike | null>(null);

  const report = useCallback(() => {
    const data: ViewerReport = {
      ready: readyRef.current,
      visible: document.visibilityState === "visible",
      fullscreen: !!document.fullscreenElement,
      fps: fpsRef.current,
    };
    sendViewerReport(data);
  }, [fpsRef]);

  const keepScreenOn = useCallback(() => {
    const wakeLock = (navigator as Navigator & {
      wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinelLike> };
    }).wakeLock;
    if (!wakeLock || document.visibilityState !== "visible") return;
    wakeLock
      .request("screen")
      .then((sentinel) => {
        wakeLockRef.current = sentinel;
      })
      .catch(() => {
        /* niet ondersteund of geweigerd: de sessie werkt ook zonder */
      });
  }, []);

  const goFullscreen = useCallback(() => {
    document.documentElement.requestFullscreen?.().catch(() => {
      /* bv. iPhone: de pagina vult het venster ook zonder echt volledig scherm */
    });
  }, []);

  // Engine en verbinding starten direct, zodat de behandelaar ziet dat de link is geopend.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;

    document.body.classList.add("fullscreen-mode", "emdr-client-mode");
    if (!host.dataset.booted) {
      host.innerHTML = bodyHtml;
      host.dataset.booted = "1";
    }

    void bootstrapViewerEmdr().then(() => {
      if (cancelled) return;
      connectViewer(code, {
        onStatus: (status) => {
          setWsStatus(status);
          if (status === "connected") report();
        },
        onPlayingChange: setPlaying,
        onSudChange: setSudOpen,
        onEnded: () => {
          setEnded(true);
          // Uit volledig scherm, zodat de cliënt het tabblad kan sluiten.
          if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => {});
          void wakeLockRef.current?.release().catch(() => {});
        },
      });
    });

    return () => {
      cancelled = true;
      disconnectViewer();
      document.body.classList.remove("fullscreen-mode", "emdr-client-mode");
    };
  }, [code, report]);

  useEffect(() => {
    const onChange = () => {
      setFullscreen(!!document.fullscreenElement);
      if (document.visibilityState === "visible" && readyRef.current) keepScreenOn();
      report();
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("visibilitychange", onChange);
    const interval = window.setInterval(report, REPORT_INTERVAL_MS);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("visibilitychange", onChange);
      window.clearInterval(interval);
      void wakeLockRef.current?.release().catch(() => {});
    };
  }, [report, keepScreenOn]);

  const handleReady = () => {
    // Alles wat de browser alleen na een klik toestaat, gebeurt hier in één keer.
    window.__EMDR_UNLOCK_AUDIO__?.();
    goFullscreen();
    keepScreenOn();
    readyRef.current = true;
    setReady(true);
    report();
  };

  useEffect(() => {
    if (playing) setHasPlayed(true);
  }, [playing]);

  const paused = ready && !ended && !playing && !sudOpen;
  const waiting = paused && !hasPlayed;
  const reconnecting = wsStatus === "reconnecting" || wsStatus === "connecting";

  return (
    <div className="emdr-client">
      <div className="emdr-client-stage jari-tool-emdr" ref={hostRef} />

      {ended && (
        <div className="emdr-client-welcome" role="dialog" aria-labelledby="emdr-client-ended-title">
          <div className="emdr-client-welcome-card">
            <h1 id="emdr-client-ended-title">De sessie is afgelopen</h1>
            <p>Je kunt dit tabblad nu sluiten.</p>
          </div>
        </div>
      )}

      {!ready && !ended && (
        <div className="emdr-client-welcome" role="dialog" aria-labelledby="emdr-client-title">
          <div className="emdr-client-welcome-card">
            <h1 id="emdr-client-title">EMDR met je behandelaar</h1>
            <p>
              Straks zie je hier een bal bewegen. Je hoeft alleen de instructies van je behandelaar
              te volgen.
            </p>
            <button type="button" className="emdr-client-ready-btn" onClick={handleReady}>
              Ik ben er klaar voor
            </button>
            <p className="emdr-client-welcome-hint">
              Je scherm wordt helemaal gevuld. Je behandelaar blijf je horen.
            </p>
          </div>
        </div>
      )}

      {waiting && (
        <div className="emdr-client-waiting" role="status">
          <p className="emdr-client-waiting-title">Je behandelaar start zo</p>
          <p>Je hoeft niets te doen. Je behandelaar blijf je horen.</p>
          {!fullscreen && (
            <button type="button" className="emdr-client-fullscreen-btn" onClick={goFullscreen}>
              Maak het scherm weer groot
            </button>
          )}
          {reconnecting && (
            <p className="emdr-client-reconnecting">De verbinding wordt hersteld…</p>
          )}
        </div>
      )}

      {paused && hasPlayed && !fullscreen && (
        <button
          type="button"
          className="emdr-client-fullscreen-btn emdr-client-fullscreen-btn--corner"
          onClick={goFullscreen}
        >
          Maak het scherm weer groot
        </button>
      )}
    </div>
  );
}
